import { createSlice } from '@reduxjs/toolkit';
import { GenericEntityState, ResponseAction } from '../types';
import createFetchReducer from '../../utils/createFetchReducer';
import { getFunnelsTypes, getFunnelTypes } from './action';
import { FunnelType } from '@app/components/modules/funnel/types';
import { CtaHiddenUpdate } from '../cta/types';

const initialState: GenericEntityState = {
	loading: false,
	funnels: [],
	currentFunnel: null,
	error: {},
	response: {
		status: null,
		message: null,
	},
};

export type funnelStateType = typeof initialState;

function getFunnelsMapper(state: funnelStateType, action: ResponseAction) {
	const { funnels } = action.response;
	state.funnels = funnels;
}

function getFunnelMapper(state: funnelStateType, action: ResponseAction) {
	const { funnel } = action.response;
	state.currentFunnel = funnel;
}

const funnelSlice = createSlice({
	name: 'funnel',
	initialState,
	reducers: {
		RESET(state: funnelStateType) {
			state.funnels = [];
		},
		UPDATE_FUNNEL_CAMPAIGN_NICHE_INBOX_CONTENT_TEXT(
			state: funnelStateType,
			action: {
				payload: {
					funnelId: string;
					campaignId: string;
					text: string;
				};
			},
		) {
			const { funnelId, campaignId, text } = action.payload;

			const updateCampaignText = (funnel: FunnelType) => {
				const campaign = funnel.campaigns.find((c) => c._id === campaignId);
				if (!campaign) return;

				campaign.nicheInboxMessageContent = {
					...campaign.nicheInboxMessageContent,
					text,
				};
			};

			const stateFunnel = state.funnels.find(
				(f: FunnelType) => f._id === funnelId,
			);
			const currentFunnelMatchesID =
				(state.currentFunnel as FunnelType)?._id === funnelId;
			const hasFunnel = Boolean(stateFunnel) || currentFunnelMatchesID;
			const funnelToUpdate = stateFunnel || (state.currentFunnel as FunnelType);

			if (hasFunnel) {
				updateCampaignText(funnelToUpdate);
			}
		},
		UPDATE_FUNNEL_CAMPAIGN_CTA(
			state: funnelStateType,
			action: {
				payload: {
					funnelId: string;
					campaignId: string;
					ctaId: string;
					messageType: 'dueDateNudge';
					text: string;
				};
			},
		) {
			const { funnelId, campaignId, ctaId, messageType, text } = action.payload;
			const funnel: FunnelType = state.funnels.find(
				(f: FunnelType) => f._id === funnelId,
			);
			if (!funnel) return;

			const campaign = funnel.campaigns.find((c) => c._id === campaignId);
			if (campaign?.callToActions) {
				const ctaIndex = campaign.callToActions.findIndex(
					(c) => c._id === ctaId,
				);
				if (ctaIndex !== -1 && campaign.callToActions[ctaIndex][messageType]) {
					campaign.callToActions[ctaIndex][messageType].text = text;
				}
			}
		},
		UPDATE_FUNNEL_CAMPAIGN_CTA_HIDDEN(
			state: funnelStateType,
			action: {
				payload: {
					funnelId: string;
					campaignId: string;
					updates: CtaHiddenUpdate[];
				};
			},
		) {
			const { funnelId, campaignId, updates } = action.payload;
			const funnel: FunnelType = state.funnels.find(
				(f: FunnelType) => f._id === funnelId,
			);
			if (!funnel) return;

			const campaign = funnel.campaigns.find((c) => c._id === campaignId);
			const { callToActions } = campaign ?? {};
			if (callToActions) {
				updates.forEach(({ ctaId, hidden }) => {
					const ctaIndex = callToActions.findIndex((c) => c._id === ctaId);
					if (ctaIndex !== -1) {
						callToActions[ctaIndex].hidden = hidden;
					}
				});
			}
		},
	},
	extraReducers: {
		...createFetchReducer(getFunnelsTypes, getFunnelsMapper),
		...createFetchReducer(getFunnelTypes, getFunnelMapper),
	},
});

export default funnelSlice.reducer;
